import { observer } from 'mobx-react';
import { Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Routes } from '../../../../route/Routes';
import { useUserMeStore, useEventStore } from '../../../../store';
import {
  Container,
  Flex,
  Space,
  Divider,
  ScrollArea,
  Text,
  Loader,
} from '@mantine/core';
import classes from './BookInPerson.module.css';
import { Banner, Title } from '../../../../component';
import { useWindowSize } from '../../../../hooks';
import { BookingHeader } from './BookingHeader';
import { MapWrapper } from './MapWrapper';
import { BookingItemList } from './BookingItem';
import { splitLocationString } from '../../../../util/appointment/splitLocationString';
import { Header } from '../../../public/navigation';
import { useTranslation } from 'react-i18next';
import { FeatureFlag } from '@web/common';
import { isFeatureEnabled } from '../../../../util/ff/isFeatureEnabled';

export const BookInPerson = observer(() => {
  // when booking is not allowed, we navigate back to /study
  // users can only reach this endpoint by manually typing it
  if (!isFeatureEnabled(FeatureFlag.BookingAllowed)) {
    return <Navigate to={Routes.Study} />;
  }
  const userMeStore = useUserMeStore();
  const eventStore = useEventStore();
  const isSmallScreen = useWindowSize(650);
  const [selectedTeamSlug, setSelectedTeamSlug] = useState<null | string>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<null | number>(null);
  const [selectedLocation, setSelectedLocation] = useState<null | string>(null);
  const { t } = useTranslation('appointment-booking');

  useEffect(() => {
    userMeStore.loadMyData();
  }, [userMeStore]);

  useEffect(() => {
    eventStore.loadEvents();
  }, [eventStore]);

  useEffect(() => {
    if (eventStore.events[0]?.team?.slug && eventStore.events[0]?.team?.id) {
      setSelectedTeamSlug(eventStore.events[0]?.team?.slug);
      setSelectedTeamId(eventStore.events[0]?.id);
      // is needed to update map location
      setSelectedLocation(
        splitLocationString(eventStore.events[0]?.locations).link,
      );
    }
  }, [eventStore.events]);

  if (!userMeStore.myInfo && !userMeStore.loadingInfo) {
    return <Navigate to={Routes.Study} />;
  }

  const bookingList = (
    <BookingItemList
      // we only want to list teams, that we have in our mapping
      data={eventStore.events}
      selectedTeamSlug={selectedTeamSlug}
      setSelectedTeamSlug={setSelectedTeamSlug}
      selectedEventSlug={selectedTeamId}
      setSelectedEventSlug={setSelectedTeamId}
      selectedLocation={selectedLocation}
      setSelectedLocation={setSelectedLocation}
    />
  );

  const leftSide = (
    <>
      <BookingHeader count={eventStore.events.length} />
      {isSmallScreen && <MapWrapper location={selectedLocation} />}
      <Divider color="#B1B1B1" />

      {isSmallScreen ? (
        bookingList
      ) : (
        <ScrollArea
          mah="500px"
          scrollbarSize={8}
          type="always"
          offsetScrollbars
        >
          {bookingList}
        </ScrollArea>
      )}
    </>
  );

  return (
    <>
      <Header canLogout />
      <Title width={900} text={t('Title')} />
      {!isSmallScreen && <Space h="xl" />}

      <Container fluid className={classes.container}>
        <Flex className={classes.contentWrapper}>
          <Banner padding={isSmallScreen ? '0px 20px' : '0px 0px 20px 0px'}>
            <Text fw="600">{t('Banner.Title')}</Text>
            <Text>{t('Banner.Description')}</Text>
            <Space h="md" />
            <Text>{t('Banner.Description2')}</Text>
          </Banner>
          <Flex className={classes.leftSide}>
            {eventStore.loadingEvents ? (
              <div className={classes.loadingContainer}>
                <Loader />
              </div>
            ) : (
              leftSide
            )}
          </Flex>

          {!isSmallScreen && <MapWrapper location={selectedLocation} />}
        </Flex>
      </Container>
    </>
  );
});
